import React, { useContext, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import _ from 'lodash';
import moment from 'moment';
import { renderToString } from 'react-dom/server';

import abbreviateNumber from 'utils/abbreviateNumber';
import removeTodayIfItIsZero from '../../../../removeTodayIfItIsZero';
import removeFutureValues from '../../../../removeFutureValues';
import useV5ChartData from 'hooks/useV5ChartData';
import timeSeriesOptions from '../../highchartOptions/timeSeriesOptions';
import AccountContext from 'contexts/AccountContext';
import { useChartPalette } from 'contexts/ChartPaletteContext';
import { ChartColors } from '../../../../theme/tokens';
import formatFloat from '../../../../api/getChartSeries/formatFloat';
import base from '../../highchartOptions/baseOptions';
import ComparisonContext from 'contexts/ComparisonContext';
import useChartDrillDowns from 'hooks/useChartDrillDowns';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import toAutoInterval from '../../toAutoInterval';
import getGroupByField from 'hooks/useMetricMatrix/getGroupByField';
import getCurrentDateTimeFor from '../../getCurrentDateTimeFor';
import buildTooltipFormatter from '../../tooltipFormatter';
import formatDateLabel from '../../formatDateLabel';
import removePartialIntervals, {
  removePartialFirstInterval,
} from '../../../../removePartialIntervals';
import useDateScope from 'hooks/useDateScope';
import useValueFormatters from 'hooks/useValueFormatters';
import isComparisonSeriesItem from 'isComparisonSeriesItem';
import describeComparison from 'formatters/describeComparison';
import {
  findSeriesDef,
  getSeriesDataKey,
} from 'components/V5Gadget/seriesDataKey';
import { FiscalPeriodsContext } from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/FiscalPeriodsProvider';

const isSingleMetricAcrossDimension = (chartDef: V5ChartDefinition) =>
  chartDef.dimensionB !== undefined && chartDef.series.length === 1;

const isWithoutExtraDimension = (chartDef: V5ChartDefinition) =>
  chartDef.dimensionB === undefined;

export const shouldUseComparisonTooltip = ({
  chartDef,
  comparison,
}: {
  chartDef: V5ChartDefinition;
  comparison?: PersistedComparisonType;
}) => {
  return (
    !isSingleMetricAcrossDimension(chartDef) &&
    !!comparison &&
    chartDef.series.length === 1
  );
};

// https://api.highcharts.com/class-reference/Highcharts#.TooltipFormatterCallbackFunction
const buildMultipleSeriesFormatter = (
  formatMetric: (args: { metricId: string; value: number }) => string,
  showHours?: boolean,
  interval?: ExtendedInterval | FleetOps.Interval,
) => {
  return function () {
    // @ts-ignore
    const { points } = this as Highcharts.TooltipFormatterContextObject;
    if (!points || points.length === 0) {
      return '';
    }
    // @ts-ignore
    const datetime = points[0].point['datetime'] || (points[0].x as number);
    const date = (() => {
      if (
        interval &&
        interval !== 'fiscalPeriod' &&
        interval !== 'fiscalQuarter' &&
        interval !== 'fiscalYear'
      ) {
        const isoDate = moment.utc(datetime).format('YYYY-MM-DD');
        return formatDateLabel(isoDate, interval, true);
      }
      return moment
        .utc(datetime)
        .format(showHours ? 'YYYY-MM-DD ha' : 'YYYY-MM-DD');
    })();
    return renderToString(
      <div>
        <h3>{date}</h3>
        <br />
        {points.map((p) => {
          // @ts-ignore
          const { metricId } = p.series.userOptions;
          const value = formatMetric({ value: p.y || 0, metricId });

          return (
            <React.Fragment key={Math.random()}>
              <span
                key={p.key}
                style={{
                  color: typeof p.color === 'string' ? p.color : undefined,
                }}
              >
                ●{' '}
              </span>
              <span style={{ color: 'var(--color-text-tertiary)' }}>
                {p.series.name}:{' '}
              </span>
              <b>{value}</b>
              <br />
            </React.Fragment>
          );
        })}
      </div>,
    );
  };
};

const toKey = (s: HistogramSeriesItem | ComparisonSeriesItem): string =>
  s.seriesKey || s.metricId;

const getSeriesWithoutExtraDimension = (
  data: V5ChartData,
  chartDef: V5ChartDefinition,
  isComparison?: boolean,
  interval?: FleetOps.Interval | FiscalInterval,
  fiscalRanges?: FixedPeriods.AllFiscals,
  colors: string[] = ChartColors,
) => {
  return Object.entries(data).map(
    ([seriesKey, { name, metric, response }], index) => {
      const d = Object.values(response).map(
        (i) =>
          [new Date(i.date as string).getTime(), i[metric.id] as number] as [
            number,
            number,
          ],
      );
      const dateScope = (() => {
        try {
          let ds = undefined as DateRangeInput | undefined;
          Object.values(data).forEach((item) => {
            item.response.forEach((r) => {
              ds = r['dateScope'] as DateRangeInput | undefined;
              if (ds !== undefined) {
                return ds;
              }
            });
          });

          return ds;
        } catch (ex) {
          return undefined;
        }
      })();

      const filteredValues = chartDef.excludePartialIntervals
        ? removePartialIntervals(
            removeTodayIfItIsZero(d),
            interval,
            dateScope,
            fiscalRanges,
          )
        : removePartialFirstInterval(
            removeTodayIfItIsZero(d),
            interval,
            dateScope,
          );
      const formattedValues = filteredValues.map(([date, v]) => [
        date,
        formatFloat(v, metric.formatting.precision),
      ]);

      return {
        name: isComparison ? `${name} - Last Period` : name,
        data: formattedValues,
        marker: {
          enabled: false,
          symbol: 'circle',
          fillColor: 'var(--color-bg-primary)',
          lineWidth: 1,
          lineColor: colors[index],
          radius: 3,
        },
        interval: chartDef.trendByCalendarInterval,
        dashStyle: isComparison ? 'Dash' : 'Solid',
        metricId: metric.id,
        seriesKey,
        queryOverrides: findSeriesDef(chartDef, seriesKey)?.queryOverrides,
        // Each metric's colour by its position, as its marker's. Without it the
        // chart counts on from the series it's had (it's updated in place), so
        // a metric added again takes the next colour along.
        colorIndex: index,
      } as HistogramSeriesItem;
    },
  );
};

const getSeriesForSingleMetricAcrossDimension = (
  data: V5ChartData,
  dimension: Dimension,
  series: V5ChartDefinitionSeries,
  chartDef: V5ChartDefinition,
  interval?: FleetOps.Interval | FiscalInterval,
  colors: string[] = ChartColors,
) => {
  const groupKey = dimension.field;
  const { metricId } = series;
  const metricData = data[getSeriesDataKey(series)];
  const dateScope = (() => {
    try {
      return Object.values(data)[0].response[0].dateScope as DateRangeInput;
    } catch (ex) {
      return undefined;
    }
  })();

  return Object.values(metricData.response).map(
    (dimensionSegment: any, index) => {
      const segmentName = dimensionSegment[groupKey];
      const d = Object.values(dimensionSegment.values).map(
        (i: any) =>
          [new Date(i.date).getTime(), i[metricId] as number] as [
            number,
            number,
          ],
      );
      const filteredValues = removePartialIntervals(
        removeTodayIfItIsZero(removeFutureValues(d)),
        interval,
        dateScope,
      );
      const formattedValues = filteredValues.map(([date, v]) => [
        date,
        formatFloat(v, metricData.metric.formatting.precision),
      ]);

      return {
        name: segmentName,
        data: formattedValues,
        marker: {
          enabled: false,
          symbol: 'circle',
          fillColor: 'var(--color-bg-primary)',
          lineWidth: 1,
          lineColor: colors[index],
          radius: 3,
        },
        interval: chartDef.trendByCalendarInterval,
        metricId,
      } as HistogramSeriesItem;
    },
  );
};

export const getSeriesFor = (
  data: V5ChartData,
  chartDef: V5ChartDefinition,
  comparisonData?: V5ChartData,
  dateRange?: DateRangeInput,
  comparison?: PersistedComparisonType,
  interval?: FleetOps.Interval | FiscalInterval,
  fiscalRanges?: FixedPeriods.AllFiscals,
  // The colours the chart draws with (see ChartPaletteContext)
  colors: string[] = ChartColors,
): (HistogramSeriesItem | ComparisonSeriesItem)[] => {
  try {
    if (isSingleMetricAcrossDimension(chartDef)) {
      const dimension = chartDef.dimensionB as Dimension;
      const series = chartDef.series[0];
      return getSeriesForSingleMetricAcrossDimension(
        data,
        dimension,
        series,
        chartDef,
        interval,
        colors,
      );
    }

    if (isWithoutExtraDimension(chartDef)) {
      const thisPeriod = getSeriesWithoutExtraDimension(
        data,
        chartDef,
        false,
        interval,
        fiscalRanges,
        colors,
      );
      if (comparisonData && comparison && dateRange) {
        const comparePeriod = getSeriesWithoutExtraDimension(
          comparisonData,
          chartDef,
          true,
          interval,
          fiscalRanges,
          colors,
        );
        const toComparisonSeries = (
          s: HistogramSeriesItem,
          index: number,
          isComparison?: boolean,
        ): ComparisonSeriesItem => {
          const data = s.data.map(([datetime, value], index) => {
            return {
              x: index,
              y: value,
              datetime,
            };
          });

          if (isComparison) {
            // Series with query overrides have no comparison, so pair by key
            const thisIndex = thisPeriod.findIndex(
              (t) => t.seriesKey === s.seriesKey,
            );
            const thisPeriodS = thisPeriod[thisIndex];
            // Named for what it's against, e.g. "Revenue vs previous period"
            const against = describeComparison(comparison);
            return {
              ...s,
              name:
                against && thisPeriodS
                  ? `${thisPeriodS.name} ${against}`
                  : s.name,
              data: thisPeriodS ? data.slice(0, thisPeriodS.data.length) : data,
              colorIndex: thisIndex >= 0 ? thisIndex : index,
              opacity: 1,
            };
          }

          return {
            ...s,
            data,
            colorIndex: index,
            opacity: 1,
          };
        };

        return [
          ...thisPeriod.map((s, index) => toComparisonSeries(s, index)),
          ...comparePeriod.map((s, index) =>
            toComparisonSeries(s, index, true),
          ),
        ];
      } else {
        return thisPeriod;
      }
    }
  } catch (ex) {
    return [];
  }

  throw new Error('Unknown chart definition pattern');
};

const useTimeSeriesData = ({
  chartDefinition,
  chartRef,
}: {
  chartDefinition: V5ChartDefinition;
  chartRef: React.MutableRefObject<any>;
}): {
  options: Highcharts.Options | undefined;
  isNoData: boolean;
  comparison: PersistedComparisonType | undefined;
} => {
  const { unitsLocale } = useContext(AccountContext);
  const { currentComparison: comparison } = useContext(ComparisonContext);
  const { openMenu } = useContext(DashboardContextMenuContext);
  const { allFiscals } = useContext(FiscalPeriodsContext);
  const { colors } = useChartPalette();

  const [autoInterval, setAutoInterval] = useState<AutoInterval | undefined>();
  const groupField = getGroupByField(chartDefinition, autoInterval);
  const [options, setOptions] = useState<Highcharts.Options | undefined>();
  const [isNoDataToDisplay, setIsNoDataToDisplay] = useState<boolean>(false);

  const dateRange = useDateScope({});
  const { data, comparisonData } = useV5ChartData(
    chartDefinition,
    autoInterval,
  );
  const { onDrillDown, syncChart } = useChartDrillDowns(
    groupField,
    chartRef,
    'bar',
    chartDefinition,
    autoInterval,
  );
  const { formatMetric } = useValueFormatters();

  useEffect(() => {
    if (!data) {
      return;
    }
    if (chartDefinition.trendByCalendarInterval !== 'auto') {
      return;
    }

    const aI: string | undefined = (() => {
      try {
        const firstItem = Object.values(data)[0].response[0];
        const maybeInterval = firstItem.values
          ? firstItem.values[0].interval
          : firstItem.interval;

        if (typeof maybeInterval === 'string') {
          return maybeInterval;
        }
      } catch (ex) {
        return undefined;
      }
      return undefined;
    })();

    const newAI = toAutoInterval(aI);
    setAutoInterval((currentAi) =>
      _.isEqual(currentAi, newAI) ? currentAi : newAI,
    );
  }, [chartDefinition.trendByCalendarInterval, data]);

  useEffect(() => {
    syncChart();
  }, [syncChart]);

  useEffect(() => {
    if (!data) {
      setIsNoDataToDisplay(false);
      return;
    }

    let interval: ExtendedInterval | FleetOps.Interval | undefined;
    if (chartDefinition.trendByFiscalInterval) {
      interval = chartDefinition.trendByFiscalInterval;
    } else {
      interval = autoInterval
        ? autoInterval.interval
        : chartDefinition.trendByCalendarInterval;
    }

    const series = getSeriesFor(
      data,
      chartDefinition,
      comparisonData,
      dateRange,
      comparison,
      interval,
      allFiscals,
      colors,
    );

    const getYAxisFor = (
      data: V5ChartData,
      chartDef: V5ChartDefinition,
      processedSeries: (HistogramSeriesItem | ComparisonSeriesItem)[],
    ) => {
      return Object.entries(data).map(([seriesKey]) => {
        const seriesDef = findSeriesDef(chartDef, seriesKey);

        const minRange = (() => {
          if (seriesDef?.minRangePercent === undefined) {
            return undefined;
          }
          const matchingSeries = processedSeries.find(
            (s) => toKey(s) === seriesKey && s.dashStyle !== 'Dash',
          );
          if (!matchingSeries) {
            return undefined;
          }
          const values = matchingSeries.data.map((d) =>
            Array.isArray(d) ? d[1] : d.y,
          );
          const validValues = values.filter((v) => v !== null && !isNaN(v));
          if (validValues.length === 0) {
            return undefined;
          }
          const maxVal = Math.max(...validValues);
          if (maxVal <= 0) {
            return undefined;
          }
          return maxVal * (seriesDef.minRangePercent / 100);
        })();

        return {
          max: seriesDef?.maxY,
          min: seriesDef?.minY,
          minRange,
        };
      });
    };

    const yAxis = getYAxisFor(data, chartDefinition, series);

    const getYAxisIndexFor = (
      s: HistogramSeriesItem | ComparisonSeriesItem,
    ): number => {
      return series
        .filter((s) => s.dashStyle !== 'Dash')
        .findIndex((item) => toKey(item) === toKey(s));
    };

    const newOptions = (() => {
      if (comparison && comparisonData && dateRange) {
        return {
          ...timeSeriesOptions({
            title: ' ',
            showLegend:
              isSingleMetricAcrossDimension(chartDefinition) ||
              chartDefinition.series.length > 1,
            openMenu,
            chartDef: chartDefinition,
            dateScope: dateRange,
            autoInterval,
            fontSize: 11,
            formatMetric,
          }),
          series: series.map((s) => {
            const yAxis = ((): number => {
              if (isComparisonSeriesItem(s)) {
                const histogramSeriesItem = series.find(
                  (item) => toKey(item) === toKey(s) && item.name !== s.name,
                );

                if (!histogramSeriesItem) {
                  throw new Error('histogramSeriesItem is not defined');
                }

                return getYAxisIndexFor(histogramSeriesItem);
              }

              return getYAxisIndexFor(s);
            })();

            return {
              ...s,
              onDrillDown,
              animation: false,
              yAxis,
            };
          }),
          xAxis: {
            tickPixelInterval: interval === 'hour' ? 200 : 100,
            type: 'datetime',
            padding: 11,
            labels: {
              style: {
                whiteSpace: 'nowrap',
                fontSize: 11,
              },
              formatter: function () {
                // @ts-ignore
                const x = this.value as number;
                try {
                  const baseMs = getCurrentDateTimeFor(x, series);
                  // Weeks are labelled by their ending date.
                  const displayMs =
                    interval === 'week'
                      ? baseMs + 6 * 24 * 60 * 60 * 1000
                      : baseMs;
                  return Highcharts.dateFormat(
                    `${interval === 'hour' ? '%m/%d %kh' : '%m/%e'}`,
                    displayMs,
                  );
                } catch (ex) {
                  console.warn(ex.message);
                  // Prevent crash on date change
                  return x;
                }
              },
            },
            gridLineWidth: 1,
            dateTimeLabelFormats: {
              // don't display the dummy year
              month: '%m/%e',
              year: '%b',
            },
          },
          yAxis: (() => {
            if (yAxis) {
              return yAxis.map((axis) => ({
                ...axis,
                startOnTick: false,
                title: { enabled: false },
                labels: {
                  style: { fontSize: 11 },
                  formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                  ) {
                    const abbreviated = abbreviateNumber(this.value as number);
                    if (abbreviated) {
                      return abbreviated.abbreviated.toLowerCase();
                    }
                    return this.axis.defaultLabelFormatter.call(this);
                  },
                },
              }));
            }

            return {
              title: { enabled: false },
              labels: {
                style: { fontSize: 11 },
                formatter: function (
                  this: Highcharts.AxisLabelsFormatterContextObject,
                ) {
                  const abbreviated = abbreviateNumber(this.value as number);
                  if (abbreviated) {
                    return abbreviated.abbreviated.toLowerCase();
                  }
                  return this.axis.defaultLabelFormatter.call(this);
                },
              },
            };
          })(),
          tooltip: shouldUseComparisonTooltip({
            chartDef: chartDefinition,
            comparison,
          })
            ? {
                ...base(formatMetric).tooltip,
                useHTML: true,
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border-primary)',
                style: { color: 'var(--color-text-primary)' },
                formatter: buildTooltipFormatter({
                  series,
                  autoInterval,
                  data,
                  formatMetric,
                }),
              }
            : {
                ...base(formatMetric).tooltip,
                formatter: buildMultipleSeriesFormatter(
                  formatMetric,
                  false,
                  interval,
                ),
              },
        };
      } else {
        return {
          ...timeSeriesOptions({
            title: ' ',
            showLegend:
              isSingleMetricAcrossDimension(chartDefinition) ||
              chartDefinition.series.length > 1,
            openMenu,
            chartDef: chartDefinition,
            dateScope: dateRange,
            autoInterval,
            fontSize: 11,
            formatMetric,
          }),
          series: series.map((s) => {
            const yAxis = ((): number => {
              if (isComparisonSeriesItem(s)) {
                const histogramSeriesItem = series.find(
                  (item) => toKey(item) === toKey(s) && item.name !== s.name,
                );

                if (!histogramSeriesItem) {
                  throw new Error('histogramSeriesItem is not defined');
                }

                return getYAxisIndexFor(histogramSeriesItem);
              }

              return getYAxisIndexFor(s);
            })();

            return {
              ...s,
              onDrillDown,
              animation: false,
              yAxis,
            };
          }),
          xAxis: {
            type: 'datetime',
            padding: 11,
            step: Math.ceil(series[0] ? series[0].data.length / 7 : 2),
            labels: {
              style: {
                whiteSpace: 'nowrap',
                fontSize: 11,
              },
              formatter: function () {
                // @ts-ignore
                const x = this.value as number;
                try {
                  const baseMs = getCurrentDateTimeFor(x, series);
                  // Weeks are labelled by their ending date.
                  const displayMs =
                    interval === 'week'
                      ? baseMs + 6 * 24 * 60 * 60 * 1000
                      : baseMs;
                  return Highcharts.dateFormat(
                    `${interval === 'hour' ? '%m/%d %kh' : '%m/%e'}`,
                    displayMs,
                  );
                } catch (ex) {
                  console.warn(ex.message);
                  // Prevent crash on date change
                  return x;
                }
              },
            },
          },
          yAxis: (() => {
            if (yAxis) {
              return yAxis.map((axis) => ({
                ...axis,
                startOnTick: false,
                title: { enabled: false },
                labels: {
                  style: { fontSize: 11 },
                  formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                  ) {
                    const abbreviated = abbreviateNumber(this.value as number);
                    if (abbreviated) {
                      return abbreviated.abbreviated.toLowerCase();
                    }
                    return this.axis.defaultLabelFormatter.call(this);
                  },
                },
              }));
            }

            return {
              title: { enabled: false },
              labels: {
                style: { fontSize: 11 },
                formatter: function (
                  this: Highcharts.AxisLabelsFormatterContextObject,
                ) {
                  const abbreviated = abbreviateNumber(this.value as number);
                  if (abbreviated) {
                    return abbreviated.abbreviated.toLowerCase();
                  }
                  return this.axis.defaultLabelFormatter.call(this);
                },
              },
            };
          })(),
          tooltip: {
            ...base(formatMetric).tooltip,
            formatter: buildMultipleSeriesFormatter(
              formatMetric,
              interval === 'hour',
              interval,
            ),
          },
        };
      }
    })();

    setOptions(newOptions as any as Highcharts.Options);
    setIsNoDataToDisplay(
      (series.length === 1 && series[0].data.length === 0) ||
        (series.length > 1 &&
          !series.some(
            (s: HistogramSeriesItem | ComparisonSeriesItem) =>
              s.data.length > 0,
          )),
    );
  }, [
    autoInterval,
    chartDefinition,
    comparison,
    comparisonData,
    data,
    dateRange,
    formatMetric,
    onDrillDown,
    openMenu,
    unitsLocale,
    allFiscals,
    colors,
  ]);

  return { options, isNoData: isNoDataToDisplay, comparison };
};

export default useTimeSeriesData;
